import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Menu, X, ChevronRight, Star, Clock, Users, Award, CheckCircle } from "lucide-react";
import { useState, useEffect } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Scroll to top on route change
const ScrollToTop = () => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return null;
};

// Images
const IMAGES = {
  logo: `${BACKEND_URL}/api/static/gallery/0qa41gui_TFHS_Logo.png`,
  basement: `${BACKEND_URL}/api/static/gallery/ekk3ro1a_basement_pic.webp`,
  kitchen: `${BACKEND_URL}/api/static/gallery/k3dnlld0_kitchen_pic.jpg`,
  framing: `${BACKEND_URL}/api/static/gallery/p5suga3m_45F1B17D-6329-49BA-AC41-2F287FA178D4.jpeg`,
  welding: `${BACKEND_URL}/api/static/gallery/n9bdxxdp_9EF5C4EC-9D73-4AA5-A289-43170278E15C.jpeg`,
  garage: `${BACKEND_URL}/api/static/gallery/z5rzgzth_garage_photos.webp`,
  professional: `${BACKEND_URL}/api/static/gallery/jj90q0gl_167084189_m-1200x520.jpg`,
  tammy: `${BACKEND_URL}/api/static/gallery/vgw0nb18_6BE738CB-97E0-4D32-94A4-7FB48A0D6171.png`,
  tammyConsult: `${BACKEND_URL}/api/static/gallery/0k526e99_BBA92089-1248-4C95-B553-9A9334695FFC.png`,
  roofing: `${BACKEND_URL}/api/static/gallery/1wcyqeg8_711D9217-93C3-42D1-AD78-571FA2CF439C.jpeg`,
  outdoor: `${BACKEND_URL}/api/static/gallery/9ekilmmu_E70DF8DA-AF48-4735-AD7F-CE1433441ECC.png`,
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/services", label: "Services" },
    { path: "/gallery", label: "Gallery" },
    { path: "/testimonials", label: "Testimonials" },
    { path: "/contact", label: "Contact" },
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? "bg-white shadow-lg" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center">
            <img 
              src={IMAGES.logo} 
              alt="Total Family Home Solutions" 
              className="h-20 md:h-28 w-auto"
              data-testid="nav-logo"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-medium transition-colors ${
                  location.pathname === link.path
                    ? "text-amber-500"
                    : scrolled
                    ? "text-slate-600 hover:text-amber-500"
                    : "text-white hover:text-amber-300"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href="tel:9432551655"
              className="bg-amber-500 text-white px-6 py-2 rounded-full font-medium hover:bg-amber-600 transition-colors flex items-center gap-2"
            >
              <Phone size={18} />
              (943) 255-1655
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            className={`md:hidden ${scrolled ? "text-slate-800" : "text-white"}`}
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden bg-white rounded-lg shadow-xl mt-2 p-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-3 px-4 font-medium rounded-lg ${
                  location.pathname === link.path
                    ? "bg-amber-50 text-amber-500"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <a
              href="tel:9432551655"
              className="block mt-4 bg-amber-500 text-white px-6 py-3 rounded-full font-medium text-center"
            >
              Call (943) 255-1655
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <img 
              src={IMAGES.logo} 
              alt="Total Family Home Solutions" 
              className="h-20 w-auto mb-4 bg-white rounded-lg p-2"
            />
            <p className="text-slate-400 mb-6">
              Locally-owned and operated in the Greater Atlanta Area. We provide quality, reliable, and honest home solutions backed by over 15 years of experience.
            </p>
            <div className="flex space-x-4">
              <a href="tel:9432551655" className="bg-amber-500 text-white px-6 py-2 rounded-full font-medium hover:bg-amber-600 transition-colors">
                Call Now
              </a>
              <Link to="/contact" className="border border-white text-white px-6 py-2 rounded-full font-medium hover:bg-white hover:text-slate-900 transition-colors">
                Get Quote
              </Link>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-slate-400">
              <li><Link to="/" className="hover:text-amber-500 transition-colors">Home</Link></li>
              <li><Link to="/services" className="hover:text-amber-500 transition-colors">Services</Link></li>
              <li><Link to="/testimonials" className="hover:text-amber-500 transition-colors">Testimonials</Link></li>
              <li><Link to="/contact" className="hover:text-amber-500 transition-colors">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Contact Info</h4>
            <ul className="space-y-3 text-slate-400">
              <li className="flex items-center gap-2">
                <Phone size={18} className="text-amber-500" />
                <a href="tel:9432551655" className="hover:text-amber-500">(943) 255-1655</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={18} className="text-amber-500" />
                <a href="mailto:totalfamilyhome@gmail.com" className="hover:text-amber-500">totalfamilyhome@gmail.com</a>
              </li>
              <li className="flex items-center gap-2">
                <MapPin size={18} className="text-amber-500" />
                <span>Greater Atlanta Area</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-800 mt-12 pt-8 text-center text-slate-500">
          <p>&copy; {new Date().getFullYear()} Total Family Home Solutions. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const carouselItems = [
    { image: IMAGES.basement, title: "Basement Renovations" },
    { image: IMAGES.kitchen, title: "Kitchen Renovations" },
    { image: IMAGES.framing, title: "Framing, Electrical & Plumbing" },
    { image: IMAGES.welding, title: "Welding" },
    { image: IMAGES.professional, title: "Professional Home Solutions" },
  ];

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [carouselItems.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselItems.length) % carouselItems.length);
  };

  const services = [
    { image: IMAGES.kitchen, title: "Renovations", description: "Transform your home with complete renovations — from basements and kitchens to bathrooms and whole-house remodels tailored to your lifestyle." },
    { image: IMAGES.framing, title: "Framing, Electrical & Plumbing", description: "Complete construction services including professional framing, electrical wiring, and plumbing installations. Quality structural and mechanical work you can count on." },
    { image: IMAGES.welding, title: "Welding", description: "Professional welding services for custom metalwork, repairs, and fabrication. Precision craftsmanship for any project." },
    { image: IMAGES.garage, title: "Organization", description: "Create a clean, functional, and stress-free home with personalized organizing solutions and custom storage systems." },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${IMAGES.kitchen})`, backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Badge with ribbon */}
          <div className="inline-flex items-center mb-6">
            <div className="relative">
              <div className="bg-amber-500 text-white px-5 py-1.5 rounded-full text-sm font-medium tracking-wider flex items-center gap-2 shadow-lg">
                <Award size={16} />
                15+ Years of Trusted Service
              </div>
              {/* Ribbon tails */}
              <div className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 bg-amber-600 transform rotate-45 -z-10"></div>
              <div className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 bg-amber-600 transform rotate-45 -z-10"></div>
            </div>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-4">
            Your Home Deserves
          </h1>
          <h2 className="text-4xl md:text-6xl font-bold text-amber-400 mb-8">
            Expert Care
          </h2>
          <p className="text-xl text-slate-300 mb-4">Repair. Renovate. Restore.</p>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-10">
            Locally-owned and operated in the Greater Atlanta Area. We provide quality, reliable, and honest home solutions backed by over 15 years of experience.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-amber-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-amber-600 transition-all transform hover:scale-105 shadow-lg">
              Get Free Estimate
            </Link>
            <a href="tel:9432551655" className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-slate-900 transition-all flex items-center justify-center gap-2">
              <Phone size={20} />
              Call (943) 255-1655
            </a>
          </div>
        </div>
      </section>

      {/* Image Carousel Section */}
      <section className="relative w-full h-[70vh] overflow-hidden">
        {/* Slides */}
        {carouselItems.map((item, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === currentSlide ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={item.image}
              alt={item.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            <div className="absolute bottom-20 left-0 right-0 text-center">
              <h3 className="text-3xl md:text-4xl font-light text-white tracking-wide">{item.title}</h3>
            </div>
          </div>
        ))}

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-lg z-10"
          data-testid="carousel-prev-btn"
        >
          <ChevronRight className="rotate-180 text-slate-700" size={24} />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-lg z-10"
          data-testid="carousel-next-btn"
        >
          <ChevronRight className="text-slate-700" size={24} />
        </button>

        {/* Dot Indicators */}
        <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-2 z-10">
          {carouselItems.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentSlide
                  ? "w-8 h-2 bg-amber-500"
                  : "w-2 h-2 bg-white/70 hover:bg-white"
              }`}
              data-testid={`carousel-dot-${index}`}
            />
          ))}
        </div>
      </section>

      {/* Meet the Owner Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-amber-500 font-medium mb-2">Meet the Owner</p>
              <h2 className="text-4xl font-bold text-slate-800 mb-6">Tammy Davis</h2>
              <p className="text-lg text-slate-600 mb-6">
                With over 15 years of experience in the home services industry, Tammy Davis founded Total Family Home Solutions with a simple mission: to provide honest, reliable, and quality work that families can trust.
              </p>
              <p className="text-lg text-slate-600 mb-8">
                Born from real-life caregiving experience, Tammy understands what families truly need. Whether it's a small repair or a complete renovation, she treats every home like her own.
              </p>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-amber-500 text-white px-8 py-4 rounded-full font-semibold hover:bg-amber-600 transition-all">
                Get a Free Quote
                <ChevronRight size={20} />
              </Link>
            </div>
            <div className="relative">
              <img src={IMAGES.tammy} alt="Tammy Davis - Owner" className="rounded-2xl shadow-2xl w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Our Services</h2>
            <p className="text-slate-400 text-lg">From small repairs to complete renovations, we're here to help.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-slate-800 rounded-2xl overflow-hidden group hover:transform hover:scale-105 transition-all duration-300">
                <div className="aspect-video overflow-hidden">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
                  <p className="text-slate-400 text-sm mb-4">{service.description}</p>
                  <Link to="/services" className="text-amber-400 font-medium inline-flex items-center gap-1 hover:gap-2 transition-all">
                    Learn More <ChevronRight size={16} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/services" className="inline-flex items-center gap-2 border-2 border-amber-500 text-amber-500 px-8 py-4 rounded-full font-semibold hover:bg-amber-500 hover:text-white transition-all">
              View All Services
              <ChevronRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <img src={IMAGES.tammyConsult} alt="Tammy Davis consulting with client" className="rounded-2xl shadow-xl" />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-slate-800 mb-8">Why Choose Us?</h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <Users className="text-amber-500" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">Family-Focused Care</h3>
                    <p className="text-slate-600">Born from real-life caregiving experience, we understand what families truly need: reliability, honesty, and thoughtful solutions.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <Award className="text-amber-500" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">Quality & Integrity</h3>
                    <p className="text-slate-600">No overcharging, no overselling. Just honest, high-quality work from contractors you can trust.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <Clock className="text-amber-500" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">15+ Years Experience</h3>
                    <p className="text-slate-600">Proven expertise in home repairs, remodeling, and organization services throughout the Greater Atlanta area.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Preview */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-3xl p-10 shadow-xl">
            <div className="flex justify-center mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="text-amber-400 fill-amber-400" size={24} />
              ))}
            </div>
            <blockquote className="text-2xl text-slate-700 italic mb-6">
              "They designed a custom gate that matched our existing railing perfectly. Their professionalism and attention to detail gave me real peace of mind."
            </blockquote>
            <p className="font-semibold text-slate-800">Dave</p>
            <p className="text-slate-500">Custom Stair Gate</p>
          </div>
          <Link to="/testimonials" className="inline-flex items-center gap-2 text-amber-500 font-semibold mt-8 hover:gap-3 transition-all">
            Read More Reviews <ChevronRight size={20} />
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-amber-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Let's Get Started Today</h2>
          <p className="text-xl text-amber-100 mb-10">
            Just bought your house and trying to figure out your space? Lived in your home for years and ready for a change? We can make your home the best it can be.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-amber-500 px-10 py-4 rounded-full font-semibold text-lg hover:bg-slate-100 transition-all transform hover:scale-105 shadow-lg">
            Get Your Free Quote
            <ChevronRight size={24} />
          </Link>
        </div>
      </section>
    </div>
  );
};

// Services Page
const Services = () => {
  const serviceCategories = [
    {
      title: "Renovations",
      image: IMAGES.kitchen,
      items: [
        "Basement finishing, updating, or converting into livable space",
        "New kitchens and bathrooms in unfinished areas",
        "Kitchen remodeling: modern layouts, updated cabinetry, countertops, and fixtures",
        "Bathroom upgrades: full remodels, tile work, vanities, and improved functionality",
        "Bedroom improvements: flooring, lighting, storage, and layout enhancements",
        "Flooring installation: hardwood, laminate, vinyl, tile, and repairs",
        "Whole-house remodeling: complete transformations tailored to your lifestyle"
      ]
    },
    {
      title: "Plumbing Services",
      image: `${BACKEND_URL}/api/static/gallery/bg8ydft6_how-to-install-a-sink-drain-2718789_hero_5078-64538f6f90d545c7af0728e4bf8f894e.jpg`,
      items: ["Plumbing repairs", "Leak detection & repair", "Fixture installation", "Install and repair garbage disposals", "Drain cleaning"]
    },
    {
      title: "Electrical Services",
      image: IMAGES.basement,
      items: ["Electrical repairs", "Electrical upgrades", "Outlet & switch installation", "Lighting fixtures", "Circuit breaker services", "Safety inspections"]
    },
    {
      title: "Interior Repairs",
      image: IMAGES.kitchen,
      items: ["Drywall patching for holes or cracks", "Painting and touch-ups", "Cabinet adjustments", "Door adjustments", "Caulking and sealing"]
    },
    {
      title: "Roofing & Exterior",
      image: IMAGES.roofing,
      items: ["New roofing installation", "Roofing repairs", "Siding installation", "Siding repairs", "Gutter maintenance", "Deck or porch fixes", "Fence repair"]
    },
    {
      title: "Outdoor & Yard",
      image: IMAGES.outdoor,
      items: ["Fence repair", "Deck repair", "Porch fixes", "Outdoor structures", "Landscaping support"]
    },
    {
      title: "Organization Services",
      image: IMAGES.garage,
      items: ["Home organization", "Garage organization", "Storage solutions", "Closet systems", "Decluttering assistance"]
    },
    {
      title: "General Repairs",
      image: IMAGES.professional,
      items: ["Flooring repairs", "Loose tile fixing", "Scratched wood repair", "Carpet repairs", "General handyman services"]
    }
  ];

  const processSteps = [
    { number: "1", title: "Contact Us", description: "Call or fill out our contact form to discuss your needs." },
    { number: "2", title: "Free Estimate", description: "We'll assess your project and provide a transparent quote." },
    { number: "3", title: "Quality Work", description: "Our experienced team completes your project with care." },
    { number: "4", title: "Your Satisfaction", description: "We ensure you're completely happy with the results." }
  ];

  return (
    <div>
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">Our Services</h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            From small repairs to major renovations, we handle it all with expertise and care.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10">
            {serviceCategories.map((category, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl overflow-hidden hover:shadow-xl transition-shadow">
                <div className="aspect-video overflow-hidden">
                  <img src={category.image} alt={category.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">{category.title}</h3>
                  <ul className="space-y-2">
                    {category.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-600">
                        <CheckCircle className="text-amber-500 flex-shrink-0 mt-1" size={18} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">How We Work</h2>
            <p className="text-slate-400 text-lg">Our streamlined process ensures quality results and peace of mind.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center text-2xl font-bold text-white mx-auto mb-6">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-slate-400">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-amber-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-amber-100 mb-10">
            Contact us today for a free estimate on your project.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-amber-500 px-10 py-4 rounded-full font-semibold text-lg hover:bg-slate-100 transition-all">
            Request Free Estimate
            <ChevronRight size={24} />
          </Link>
        </div>
      </section>
    </div>
  );
};

// Testimonials Page
const Testimonials = () => {
  const stats = [
    { value: "15+", label: "Years Experience" },
    { value: "500+", label: "Projects Completed" },
    { value: "4.9", label: "Average Rating" },
    { value: "100%", label: "Satisfaction" }
  ];

  const testimonials = [
    {
      quote: "After my wife's fall, I needed help fast before traveling. They responded quickly, designed a custom gate matching our railing, and installed it so it can be removed later without damage. Their professionalism gave me real peace of mind.",
      name: "Michael R.",
      location: "Alpharetta",
      project: "Custom Stair Gate"
    },
    {
      quote: "High-quality work on our rental property refresh — bathrooms, flooring, and paint. Stayed on budget and timeline, and worked through a challenging inspection to pass on time. Would definitely work with them again.",
      name: "Sarah M.",
      location: "Johns Creek",
      project: "Rental Property Refresh"
    },
    {
      quote: "They helped design a basement kitchenette and laundry in a tight space and budget. Fit everything I needed — washer/dryer, dishwasher, cabinets, sink — while saving money. The finished area looks like it was always part of the home.",
      name: "Jennifer L.",
      location: "Cumming GA",
      project: "Basement Kitchenette"
    },
    {
      quote: "Total Family Home Solutions transformed our basement into a beautiful living space. Their attention to detail and professionalism exceeded our expectations. Highly recommend!",
      name: "Robert T.",
      location: "Roswell",
      project: "Basement Renovation"
    },
    {
      quote: "We hired them for our kitchen remodel and couldn't be happier. From free quote to final walkthrough, they made the process easy and stress-free. Outstanding quality of work.",
      name: "Amanda W.",
      location: "Suwanee",
      project: "Kitchen Remodel"
    },
    {
      quote: "Our garage was a disaster until they came in. Custom shelving and storage solutions completely transformed the space. Now we can actually park our cars in there! Professional and reliable.",
      name: "Chris B.",
      location: "Marietta",
      project: "Garage Organization"
    }
  ];

  const featuredTestimonial = {
    quote: "Total Family Home Solutions isn't just a contractor - they're part of our family now. After working with them on multiple projects, I wouldn't trust anyone else with our home.",
    name: "The Johnson Family",
    location: "Sandy Springs, GA"
  };

  return (
    <div>
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">Testimonials</h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Don't just take our word for it. Here's what our customers have to say about working with us.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-amber-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl font-bold text-white">{stat.value}</p>
                <p className="text-amber-100">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="text-amber-400 fill-amber-400" size={18} />
                  ))}
                </div>
                <p className="text-slate-600 mb-6 italic">"{testimonial.quote}"</p>
                <div>
                  <p className="font-semibold text-slate-800">{testimonial.name}</p>
                  {testimonial.location && <p className="text-slate-500 text-sm">{testimonial.location}</p>}
                  <p className="text-amber-500 text-sm font-medium mt-1">{testimonial.project}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Testimonial */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-slate-800 rounded-3xl p-12 border border-slate-700">
            <div className="flex justify-center mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="text-amber-400 fill-amber-400" size={28} />
              ))}
            </div>
            <blockquote className="text-2xl md:text-3xl text-white italic mb-8">
              "{featuredTestimonial.quote}"
            </blockquote>
            <p className="font-semibold text-amber-400 text-xl">{featuredTestimonial.name}</p>
            <p className="text-slate-400">{featuredTestimonial.location}</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-amber-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Join Our Happy Customers</h2>
          <p className="text-xl text-amber-100 mb-10">
            Experience the Total Family Home Solutions difference. Contact us today for your free quote.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-amber-500 px-10 py-4 rounded-full font-semibold text-lg hover:bg-slate-100 transition-all">
            Get Your Free Quote
            <ChevronRight size={24} />
          </Link>
        </div>
      </section>
    </div>
  );
};

// Gallery Page
const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = [
    "All",
    "Kitchen",
    "Bathroom", 
    "Basement",
    "Flooring",
    "Restoration",
    "Organization",
    "Plumbing",
    "Electrical",
    "Roofing & Exterior",
    "Hardscaping",
    "Welding"
  ];

  // Gallery images - organized by category
  const galleryImages = [
    // Kitchen
    { image: `${BACKEND_URL}/api/static/gallery/s0kdtjor_IMG_20260224_204809.jpg`, title: "Kitchen Backsplash & Cabinets", category: "Kitchen" },
    { image: `${BACKEND_URL}/api/static/gallery/oiw930mp_IMG_20260224_204813.jpg`, title: "Complete Kitchen Renovation", category: "Kitchen" },
    { image: `${BACKEND_URL}/api/static/gallery/z4n49wqd_IMG_20260224_204815.jpg`, title: "Kitchen Cabinet Installation", category: "Kitchen" },
    { image: `${BACKEND_URL}/api/static/gallery/gp0pbevl_1772653909180.png`, title: "Basement Kitchenette & Laundry", category: "Kitchen" },
    // Bathroom
    { image: `${BACKEND_URL}/api/static/gallery/02w5rl31_IMG_20260224_204820.jpg`, title: "Double Vanity Bathroom", category: "Bathroom" },
    { image: `${BACKEND_URL}/api/static/gallery/uq8n7vy0_IMG_20260224_204821.jpg`, title: "Master Bath with Jetted Tub", category: "Bathroom" },
    { image: `${BACKEND_URL}/api/static/gallery/ox67rdvp_IMG_20260224_204801.jpg`, title: "Soaking Tub Installation", category: "Bathroom" },
    { image: `${BACKEND_URL}/api/static/gallery/e6vnm1a0_IMG_20260224_204803.jpg`, title: "Marble Shower Bathroom Remodel", category: "Bathroom" },
    { image: `${BACKEND_URL}/api/static/gallery/js0ckjnb_IMG_20260224_204806.jpg`, title: "Marble Tile Shower with Niches", category: "Bathroom" },
    { image: `${BACKEND_URL}/api/static/gallery/gh2auzs2_1772506087729.png`, title: "Fire Restoration - Bathroom After", category: "Bathroom" },
    { image: `${BACKEND_URL}/api/static/gallery/svmzap85_1772506101999.png`, title: "Fire Restoration - Tub Surround After", category: "Bathroom" },
    { image: `${BACKEND_URL}/api/static/gallery/9ltuufe4_bathroom_before_demo.jpg`, title: "Bathroom Floor Remodel - Before", category: "Bathroom" },
    { image: `${BACKEND_URL}/api/static/gallery/18nxv87j_bathroom_during_tile.jpg`, title: "Bathroom Floor Remodel - During (Marble Tile)", category: "Bathroom" },
    { image: `${BACKEND_URL}/api/static/gallery/aedpbh0f_bathroom_after_complete.jpg`, title: "Bathroom Floor Remodel - After", category: "Bathroom" },
    // Basement
    { image: `${BACKEND_URL}/api/static/gallery/ha12ms9u_Screenshot_20260301_222540_Chrome.jpg`, title: "Basement Flood Restoration - After", category: "Basement" },
    { image: `${BACKEND_URL}/api/static/gallery/j0vrcy0y_Screenshot_20260301_222619_Chrome.jpg`, title: "Basement Bedroom Restored - After", category: "Basement" },
    { image: `${BACKEND_URL}/api/static/gallery/mb2vh6h6_Screenshot_20260301_222624_Chrome.jpg`, title: "Basement Living Area - After", category: "Basement" },
    { image: `${BACKEND_URL}/api/static/gallery/2j12uhdb_Screenshot_20260301_222630_Chrome.jpg`, title: "Basement with New Flooring - After", category: "Basement" },
    { image: `${BACKEND_URL}/api/static/gallery/gp0pbevl_1772653909180.png`, title: "Basement Kitchenette & Laundry Complete", category: "Basement" },
    // Restoration - Water Damage
    { image: `${BACKEND_URL}/api/static/gallery/lx3elqhu_IMG_20260301_223521.jpg`, title: "Basement Flood Damage - Before", category: "Restoration" },
    { image: `${BACKEND_URL}/api/static/gallery/fywawib8_IMG_20260301_223523.jpg`, title: "Water Heater Leak Damage - Before", category: "Restoration" },
    { image: `${BACKEND_URL}/api/static/gallery/u28j0b7q_IMG_20260301_223528.jpg`, title: "Basement Water Damage - Before", category: "Restoration" },
    { image: `${BACKEND_URL}/api/static/gallery/eykn36nj_IMG_20260301_223532.jpg`, title: "Flood Damage to Trim - Before", category: "Restoration" },
    // Flooring
    { image: `${BACKEND_URL}/api/static/gallery/mv2t5azq_IMG_20260224_204752.jpg`, title: "Tile Flooring Installation", category: "Flooring" },
    { image: `${BACKEND_URL}/api/static/gallery/xm4ffwiz_IMG_20260224_204759.jpg`, title: "Bathroom Tile Floor", category: "Flooring" },
    { image: `${BACKEND_URL}/api/static/gallery/nqfjdgax_1772506542134.png`, title: "Fire Restoration - Hardwood Floor After", category: "Flooring" },
    { image: `${BACKEND_URL}/api/static/gallery/8bx3801l_1772505705308.png`, title: "Fire Restoration - Master Bedroom Floor After", category: "Flooring" },
    // Electrical
    { image: `${BACKEND_URL}/api/static/gallery/a2nptgy4_IMG_20260301_232149.jpg`, title: "Double Tap Panel Issue - Before", category: "Electrical" },
    { image: `${BACKEND_URL}/api/static/gallery/nd2m7ulm_1772653300326.png`, title: "Double Tap Fixed & 2nd Panel Added - After", category: "Electrical" },
    { image: `${BACKEND_URL}/api/static/gallery/a6l7wem9_1772423134968.png`, title: "Recessed Lighting - Ceiling Repair & Repaint", category: "Electrical" },
    { image: `${BACKEND_URL}/api/static/gallery/atcmi28q_1772655991916.png`, title: "Dining Room Light Fixture Installation", category: "Electrical" },
    // Plumbing
    { image: `${BACKEND_URL}/api/static/gallery/qbxvmvg8_1772480262422.png`, title: "Garbage Disposal Installation", category: "Plumbing" },
    { image: `${BACKEND_URL}/api/static/gallery/28ggd7uz_1772656241970.png`, title: "Bathroom Faucet Installation", category: "Plumbing" },
    // Welding
    { image: `${BACKEND_URL}/api/static/gallery/aak4oxkv_IMG_20260224_204830.jpg`, title: "Commercial Bar - Finished", category: "Welding" },
    { image: `${BACKEND_URL}/api/static/gallery/zgmi8fxe_IMG_20260224_204832.jpg`, title: "Commercial Bar - Construction", category: "Welding" },
    // Hardscaping
    { image: `${BACKEND_URL}/api/static/gallery/vp1juczl_stone_retaining_wall_landscaping.jpg`, title: "Stone Retaining Wall with Landscaping", category: "Hardscaping" },
    { image: `${BACKEND_URL}/api/static/gallery/rutwtys6_natural_stone_retaining_wall.jpg`, title: "Natural Stone Retaining Wall", category: "Hardscaping" },
    { image: `${BACKEND_URL}/api/static/gallery/8hhvez83_stone_veneer_foundation.jpg`, title: "Stone Veneer Foundation Wall", category: "Hardscaping" },
    { image: `${BACKEND_URL}/api/static/gallery/27i9eexl_stone_veneer_patio.jpg`, title: "Stone Veneer & Flagstone Patio", category: "Hardscaping" },
    { image: `${BACKEND_URL}/api/static/gallery/nuaqq393_custom_stone_steps.jpg`, title: "Custom Stone Steps & Walls", category: "Hardscaping" },
    // Custom Work
    { image: `${BACKEND_URL}/api/static/gallery/igsxtt75_IMG_20260224_204717.png`, title: "Custom Stair Gate", category: "Organization" },
    { image: `${BACKEND_URL}/api/static/gallery/utdz7i5n_IMG_20260228_201411.jpg`, title: "Custom Outdoor Shed Build", category: "Organization" },
    // Fire Restoration (Brick House)
    { image: `${BACKEND_URL}/api/static/gallery/agx1ozxz_IMG_20260228_202457.jpg`, title: "Fire Restoration - Before", category: "Restoration" },
    { image: `${BACKEND_URL}/api/static/gallery/tkafr70p_IMG_20260228_202459.jpg`, title: "Fire Damage Roof - Before", category: "Restoration" },
    { image: `${BACKEND_URL}/api/static/gallery/fgom4mho_IMG_20260228_202501.jpg`, title: "Fire Damage Structure - Before", category: "Restoration" },
    { image: `${BACKEND_URL}/api/static/gallery/fsrphy89_image000000_1.jpg`, title: "Fire Restoration - After", category: "Restoration" },
    { image: `${BACKEND_URL}/api/static/gallery/42w1nur4_1772328137859.png`, title: "Fire Restoration Complete - After", category: "Restoration" },
    { image: `${BACKEND_URL}/api/static/gallery/sij7rj8m_1772328039452.png`, title: "Fire Restoration with Landscaping - After", category: "Restoration" },
    { image: `${BACKEND_URL}/api/static/gallery/gh2auzs2_1772506087729.png`, title: "Fire Restoration - Bathroom After", category: "Restoration" },
    { image: `${BACKEND_URL}/api/static/gallery/svmzap85_1772506101999.png`, title: "Fire Restoration - Tub Surround After", category: "Restoration" },
    { image: `${BACKEND_URL}/api/static/gallery/nqfjdgax_1772506542134.png`, title: "Fire Restoration - Hardwood Floor After", category: "Restoration" },
    { image: `${BACKEND_URL}/api/static/gallery/8bx3801l_1772505705308.png`, title: "Fire Restoration - Master Bedroom Floor After", category: "Restoration" },
    // Water Damage Restoration
    { image: `${BACKEND_URL}/api/static/gallery/lx3elqhu_IMG_20260301_223521.jpg`, title: "Basement Flood Damage - Before", category: "Restoration" },
    { image: `${BACKEND_URL}/api/static/gallery/fywawib8_IMG_20260301_223523.jpg`, title: "Water Heater Leak Damage - Before", category: "Restoration" },
    { image: `${BACKEND_URL}/api/static/gallery/u28j0b7q_IMG_20260301_223528.jpg`, title: "Basement Water Damage - Before", category: "Restoration" },
    { image: `${BACKEND_URL}/api/static/gallery/eykn36nj_IMG_20260301_223532.jpg`, title: "Flood Damage to Trim - Before", category: "Restoration" },
    { image: `${BACKEND_URL}/api/static/gallery/ha12ms9u_Screenshot_20260301_222540_Chrome.jpg`, title: "Basement Flood Restoration - After", category: "Restoration" },
    { image: `${BACKEND_URL}/api/static/gallery/j0vrcy0y_Screenshot_20260301_222619_Chrome.jpg`, title: "Basement Bedroom Restored - After", category: "Restoration" },
    { image: `${BACKEND_URL}/api/static/gallery/mb2vh6h6_Screenshot_20260301_222624_Chrome.jpg`, title: "Basement Living Area Restored - After", category: "Restoration" },
    { image: `${BACKEND_URL}/api/static/gallery/2j12uhdb_Screenshot_20260301_222630_Chrome.jpg`, title: "Basement New Flooring - After", category: "Restoration" },
    // Roofing & Exterior
    { image: `${BACKEND_URL}/api/static/gallery/tpgm574v_Screenshot_20260301_222710_Chrome.jpg`, title: "Deck Lift & Stair Rebuild - Settling Fix", category: "Roofing & Exterior" },
    { image: `${BACKEND_URL}/api/static/gallery/yssm9s9q_Screenshot_20260301_222703_Chrome.jpg`, title: "Lower Deck After Lift Repair", category: "Roofing & Exterior" },
    { image: `${BACKEND_URL}/api/static/gallery/4zxlukgm_IMG_20260224_204823.jpg`, title: "Retaining Wall Installation", category: "Roofing & Exterior" },
    { image: `${BACKEND_URL}/api/static/gallery/o2yogm51_1771625821760.png`, title: "Retaining Wall with Landscaping - After", category: "Roofing & Exterior" },
    { image: `${BACKEND_URL}/api/static/gallery/scgaev06_IMG_20260224_204825.jpg`, title: "Retaining Wall with Drainage", category: "Roofing & Exterior" },
    { image: `${BACKEND_URL}/api/static/gallery/6mc6u3lo_IMG_20260224_204827.jpg`, title: "Retaining Wall Foundation", category: "Roofing & Exterior" },
    { image: `${BACKEND_URL}/api/static/gallery/nb7oz995_IMG_20260224_204829.jpg`, title: "Backyard Pond Installation", category: "Roofing & Exterior" },
    { image: `${BACKEND_URL}/api/static/gallery/ga739i3l_IMG_20260301_230110.jpg`, title: "Ivy Removal - Before Resodding", category: "Roofing & Exterior" },
    { image: `${BACKEND_URL}/api/static/gallery/j4b1l58h_IMG_20260301_230116.jpg`, title: "New Sod Installation - After", category: "Roofing & Exterior" },
    { image: `${BACKEND_URL}/api/static/gallery/pmylfr1a_IMG_20260301_230119.jpg`, title: "Yard Resodded - After", category: "Roofing & Exterior" },
    { image: `${BACKEND_URL}/api/static/gallery/kugm55jf_Screenshot_20260301_225515_Chrome.jpg`, title: "Complete Yard Restoration - After", category: "Roofing & Exterior" },
    { image: `${BACKEND_URL}/api/static/gallery/2gk95wyt_brick_paver_walkway_install.jpg`, title: "Brick Paver Walkway - Installation", category: "Roofing & Exterior" },
    { image: `${BACKEND_URL}/api/static/gallery/dhtvm61s_brick_paver_walkway_complete.jpg`, title: "Brick Paver Walkway - Completed", category: "Roofing & Exterior" },
    { image: `${BACKEND_URL}/api/static/gallery/tlvcz3ni_tree_removal_chipper.jpg`, title: "Tree Removal Service", category: "Roofing & Exterior" },
    { image: `${BACKEND_URL}/api/static/gallery/pdrxyna7_tree_limb_removal.jpg`, title: "Large Limb Removal", category: "Roofing & Exterior" },
    { image: `${BACKEND_URL}/api/static/gallery/eko2mc0g_arborist_tree_climbing.jpg`, title: "Tree Trimming & Pruning", category: "Roofing & Exterior" },
  ];

  const filteredImages = activeCategory === "All" 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeCategory);

  return (
    <div>
      {/* Hero */}
      <section className="pt-24 pb-8 sm:pt-32 sm:pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-5xl font-bold text-white mb-3 sm:mb-6">Our Work</h1>
          <p className="text-sm sm:text-xl text-slate-300 max-w-2xl mx-auto">
            Browse through our portfolio of completed projects. From kitchens to basements, see the quality craftsmanship we bring to every home.
          </p>
        </div>
      </section>

      {/* Category Filters */}
      <section className="py-2 sm:py-6 bg-white border-b sticky top-16 sm:top-20 z-40">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
          <div className="flex sm:flex-wrap sm:justify-center gap-2 sm:gap-3 overflow-x-auto scrollbar-hide -mx-2 px-2 sm:mx-0 sm:px-0">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => {
                  setActiveCategory(category);
                  setTimeout(() => {
                    window.scrollTo({ top: document.getElementById('gallery-grid').offsetTop - 120, behavior: 'smooth' });
                  }, 100);
                }}
                className={`whitespace-nowrap flex-shrink-0 px-3 py-1.5 sm:px-6 sm:py-2 rounded-full text-xs sm:text-base font-medium transition-all ${
                  activeCategory === category
                    ? "bg-amber-500 text-white shadow-lg"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid={`gallery-filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section id="gallery-grid" className="py-6 sm:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredImages.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {filteredImages.map((item, index) => (
                <div 
                  key={index} 
                  className="group relative overflow-hidden rounded-2xl bg-white shadow-md hover:shadow-xl transition-all duration-300"
                  data-testid={`gallery-item-${index}`}
                >
                  <div className="aspect-square overflow-hidden bg-slate-100">
                    <img 
                      src={item.image} 
                      alt={item.title} 
                      className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-110" 
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <span className="inline-block px-3 py-1 bg-amber-500 text-white text-sm rounded-full mb-2">
                        {item.category}
                      </span>
                      <h3 className="text-white font-semibold text-lg">{item.title}</h3>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-24 h-24 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="text-slate-400" size={40} />
              </div>
              <h3 className="text-2xl font-semibold text-slate-700 mb-3">Gallery Coming Soon</h3>
              <p className="text-slate-500 max-w-md mx-auto">
                We're adding photos of our completed projects. Check back soon to see examples of our quality work!
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-amber-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Your Project?</h2>
          <p className="text-xl text-amber-100 mb-10">
            Let us bring the same quality and craftsmanship to your home.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-amber-500 px-10 py-4 rounded-full font-semibold text-lg hover:bg-slate-100 transition-all">
            Get Your Free Quote
            <ChevronRight size={24} />
          </Link>
        </div>
      </section>
    </div>
  );
};

// Contact Page
const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const response = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        let detail = "";
        try {
          const data = await response.json();
          detail = data?.detail || "";
        } catch (_) { /* response had no JSON body */ }
        throw new Error(detail || `Submission failed (status ${response.status}). Please try again or call us directly.`);
      }

      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting form:", error);
      setErrorMessage(
        error?.message ||
        "We couldn't send your inquiry right now. Please try again, or call us directly so your message isn't lost."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">Get In Touch</h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Start the process of turning your house into the home of your dreams by calling or emailing us today.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <div className="bg-slate-50 rounded-2xl p-8 mb-8">
                <div className="flex items-center gap-4 mb-6">
                  <img src={IMAGES.tammy} alt="Tammy Davis" className="w-20 h-20 rounded-full object-cover" />
                  <div>
                    <h3 className="text-xl font-semibold text-slate-800">Tammy Davis</h3>
                    <p className="text-slate-500">Owner & Founder</p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-900 rounded-2xl p-8 text-white">
                <h2 className="text-2xl font-bold mb-6">Contact Information</h2>
                <p className="text-slate-400 mb-8">
                  We are happy to answer any questions you may have and discuss your specific needs with you. Why wait any longer? Let us show you your home's true potential.
                </p>
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                      <Phone size={20} />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Phone</p>
                      <a href="tel:9432551655" className="text-lg font-medium hover:text-amber-400">(943) 255-1655</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                      <Mail size={20} />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Email</p>
                      <a href="mailto:totalfamilyhome@gmail.com" className="text-lg font-medium hover:text-amber-400">totalfamilyhome@gmail.com</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                      <MapPin size={20} />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Service Area</p>
                      <p className="text-lg font-medium">Greater Atlanta Area</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-slate-50 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Request a Free Estimate</h2>
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="text-green-500" size={32} />
                    </div>
                    <h3 className="text-2xl font-semibold text-slate-800 mb-2">Thank You!</h3>
                    <p className="text-slate-600">We've received your inquiry and will get back to you soon.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {errorMessage && (
                      <div
                        role="alert"
                        data-testid="contact-error-banner"
                        className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-start gap-3"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div className="text-sm">
                          <p className="font-semibold mb-1">We couldn't send your inquiry</p>
                          <p>{errorMessage}</p>
                          <p className="mt-2 text-red-700">Please try again. If the problem persists, please call us directly so we can help.</p>
                        </div>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all"
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Project Address</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all"
                        data-testid="contact-address-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Tell Me About Your Project *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all resize-none"
                        data-testid="contact-message-input"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-amber-500 text-white py-4 rounded-lg font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? "Sending..." : "Send Inquiry"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Navigation />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;
